import type {
  MeasurementDimension,
  MeasurementMetric,
  Measurement,
  WindowFunction
} from '@kentik/ui-shared/query/query.types';
import type { EventsAppProtocols, MnLookupAppProtocolColumn } from '@kentik/ui-shared/dictionary/types';

function getKey(appProtocol: string, col: MnLookupAppProtocolColumn): string {
  return `ktappprotocol__${appProtocol}__${col.custom_column}`;
}

export default function transformEventsAppProtocolMeasurements(input: EventsAppProtocols): Measurement[] {
  return Object.entries(input.cachedColumns)
    .map(([appProtocol, { metric: metrics, dimension: dimensions }]) => {
      const type = input.cachedTypes[appProtocol];
      const displayName = type?.display_name ?? appProtocol;
      const protocolId = type?.id;

      const Metrics: Record<string, MeasurementMetric> = {};
      const Dimensions: Record<string, MeasurementDimension> = {};

      for (const col of metrics) {
        const {
          custom_column,
          dimension_label,
          metadata: { is_bytes, metric_type = 'sum', hll, is_count }
        } = col;

        // Skip HLL metrics — they need generic aggregate (agg0) handling that UDE doesn't support yet
        if (!hll) {
          const colKey = getKey(appProtocol, col);
          const chfColumn = custom_column.toLowerCase();

          Metrics[colKey] = {
            Label: dimension_label,
            Column: chfColumn,
            Window: metric_type as WindowFunction,
            ToBits: is_bytes || undefined,
            Rollup: true,
            ...(is_count && chfColumn === 'i_count' && metric_type === 'sum' && { Alias: 'trautocount' })
          };
        }
      }

      for (const col of dimensions) {
        const { custom_column, dimension_label, dimension_direction, metadata } = col;
        const colKey = getKey(appProtocol, col);
        const chfColumn = custom_column.toLowerCase();

        const dim: MeasurementDimension = {
          Label: dimension_label,
          Column: chfColumn
        };

        if (dimension_direction === 'source') {
          dim.Direction = 'src';
          dim.Label = dimension_label.replace(/source\s/i, '');
        } else if (dimension_direction === 'destination') {
          dim.Direction = 'dst';
          dim.Label = dimension_label.replace(/destination\s/i, '');
        }

        // exclude snmp trap events for now
        if (metadata.lookup && protocolId !== 10033 && chfColumn !== 'int00') {
          dim.Values = {};
          for (const [code, label] of Object.entries(metadata.lookup)) {
            const numCode = parseInt(code, 10);
            if (!Number.isNaN(numCode)) {
              dim.Values[numCode] = `${label} (${code})`;
            }
          }
        }

        Dimensions[colKey] = dim;
      }

      return {
        measurement: `/events/${appProtocol}`,
        displayName,
        storage: {
          Metrics,
          Dimensions,
          ...(protocolId != null && { AppProtocol: protocolId })
        }
      } as Measurement;
    })
    .sort((a, b) => a.measurement.localeCompare(b.measurement));
}
